import React, { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams, useLocation } from "../../hooks/useRouter";
import { useDispatch, useSelector } from "react-redux";
import "./DeepInsightsChat.css";

// components
import Dashboard from "../../components/Dashboard/Dashboard";
import ChatPromptContainer from "../../components/ChatPromptContainer/ChatPromptContainer";
import MessageBox from "../../components/MessageBox/MessageBox";
import MarkdownContent from "../../components/MarkdownContent/MarkdownContent";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import MissedAlertsModal from "../../components/MissedAlertsModal/MissedAlertsModal";
import { PROMPT_CATEGORIES } from "../../components/DeepInsightsPromptLibraryModal/DeepInsightsPromptLibraryModal";
import DeepInsightsPlaybooks from "../../components/DeepInsightsPlaybooks/DeepInsightsPlaybooks";
import DeepInsightsConnectors from "../../components/DeepInsightsConnectors/DeepInsightsConnectors";
import VoiceConversationOverlay from "../../components/VoiceConversationOverlay/VoiceConversationOverlay";

// store
import {
  fetchDeepInsightsAlerts,
  fetchAllDeepInsightsAlerts,
  fetchDeepInsightsChatCompletions,
  regenerateDeepInsightsChatCompletions,
  fetchDeepInsightsUserConversation,
  fetchDeepInsightsConnectors,
  createDeepInsightsSession,
  postDeepInsightsFeedback,
} from "../../store/apiActions";

// utility & constants
import useIsMobile from "../../hooks/useIsMobile";
import { useAuth } from "../../hooks/useAuth";
import { useConstants } from "../../constant/constantProvider";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { ROUTES, SSE_EVENTS } from "../../constant/keyConstants";
import {
  generateCustomId,
  scrollToLatestViewPoint,
  generateSessionId,
  mapConversationMessages,
} from "../../utils/commonHelper";
import { encrypted, sessionManager } from "../../utils/utility";
import { handleUnauthorized } from "../../utils/apiHelper";
import { getSpeechWsUrl } from "../../store/apiActions";
import { simulateTypewriterStream } from "../../utils/streamHelper";
import { sanitizeTextPreservingHTML, sanitizeTextInput } from "../../utils/textUtils";
import textToSpeech, { computeSpokenDuration } from "../../utils/textToSpeech";
import useAlexaMode from "../../hooks/useAlexaMode";

// images
import deepInsightsIcon from "../../assets/new-images/deep-insights-logo-icon.svg";
import aiSparkleIcon from "../../assets/new-images/deep-insight-bot-icon.svg";
import downArrow from "../../assets/new-images/dropdown-icon.svg";
import pinIcon from "../../assets/new-images/pin-icon.svg";
import unpinIcon from "../../assets/new-images/unpin-icon.svg";
import voiceConversationIcon from "../../assets/new-images/voice-conversation-icon.svg";
import purpleBulbIcon from "../../assets/new-images/purple-bulb-icon.svg";

/** Build the text fed to Azure VOP — progress steps first, then the answer. */
const buildDeepInsightsVoiceContent = (pair) => {
  if (pair.role !== "assistant") return pair.content || "";

  const steps = Array.isArray(pair.steps) ? pair.steps : [];
  const stepLabels = steps
    .map((step) => (typeof step === "string" ? step : step?.label || step?.message || ""))
    .filter(Boolean);
  const answer =
    pair.content && pair.content !== "Loading..." ? pair.content.trim() : "";
  const parts = [...stepLabels, ...(answer ? [answer] : [])];

  return parts.length ? `${parts.join(". ")}.` : "";
};

const DeepInsightsChat = () => {
  const { DEEP_INSIGHTS_SUBTITLE, DEEP_INSIGHTS_ASK_PLACEHOLDER, DEEP_INSIGHTS_FOOTER_TEXT, SESSION_EXPIRED, ASK, NEW_ALERT, VIEW, VIEW_ALL_ALERTS, PIN, UNPIN, PIN_CHAT, UNPIN_CHAT } = useConstants();
  const { fullName, username, ugdnId: userUGDN } = useAuth();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscribe, emit } = useEventEmitter();
  const [searchParams, setSearchParams] = useSearchParams();
  const alertId = searchParams.get("alert_id");

  const [alertData, setAlertData] = useState(null);
  const [isAlertLoading, setIsAlertLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [uiConversation, setUIConversation] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [keepListeningWhileLoading, setKeepListeningWhileLoading] = useState(false);

  const richInputRef = useRef(null);
  const chatPromptContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const streamReaderRef = useRef(null);
  const streamAbortControllerRef = useRef(null);
  const voiceStopWithQueryTimeoutRef = useRef(null);
  const handleSendMessageRef = useRef(null);
  const connectorsRequestStartedRef = useRef(false);
  const typewriterIntervalRef = useRef(null);
  const prevConversationLengthRef = useRef(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // When true, the last assistant message will autoplay voice (set when user speaks)
  const [autoPlayLastMessage, setAutoPlayLastMessage] = useState(false);
  /** Ref mirror — readable synchronously inside async closures (avoids stale state). */
  const autoPlayLastMessageRef = useRef(false);
  const lastStoppedMessageIdRef = useRef(null);
  const [isVoiceConversationOpen, setIsVoiceConversationOpen] = useState(false);

  const hasChatMessages = uiConversation?.length > 0;

  // /dev/demo only: quick-start prompt card built from the persona's
  // example_questions (set at login — see DevDemo.jsx). Absent/empty for
  // real users since the key is only ever written by the dev-demo login.
  const devDemoCardsData = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("devDemoExampleQuestions") || "[]");
      if (!Array.isArray(stored) || stored.length === 0) return [];
      return [{
        icon: purpleBulbIcon,
        title: "Try asking",
        prompts: stored,
      }];
    } catch {
      return [];
    }
  }, []);

  const [isMissedAlertsModalOpen, setIsMissedAlertsModalOpen] = useState(false);
  const [missedAlerts, setMissedAlerts] = useState([]);
  const [isMissedAlertsLoading, setIsMissedAlertsLoading] = useState(false);

  const [showPlaybooks, setShowPlaybooks] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);
  const [deepInsightsSources, setDeepInsightsSources] = useState([]);
  const [isDeepInsightsSourcesLoading, setIsDeepInsightsSourcesLoading] = useState(true);
  // Empty means Auto; selected connector IDs are sent as connector_scope.
  const [selectedDeepInsightsSourceIds, setSelectedDeepInsightsSourceIds] = useState([]);

  const [feedbackInput, setFeedbackInput] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    document.body.classList.add('deep-insights-active');
    return () => {
      document.body.classList.remove('deep-insights-active');
    };
  }, []);

  useEffect(() => {
    if (connectorsRequestStartedRef.current) return;
    connectorsRequestStartedRef.current = true;

    const loadConnectors = async () => {
      try {
        const response = await dispatch(fetchDeepInsightsConnectors());
        const connectors = response?.connectors ?? response?.data?.connectors;
        setDeepInsightsSources(Array.isArray(connectors) ? connectors : []);
      } finally {
        setIsDeepInsightsSourcesLoading(false);
      }
    };

    loadConnectors();
  }, [dispatch]);

  useEffect(() => {
    const fetchAlertSession = async () => {
      if (!alertId) return;
      setIsAlertLoading(true);
      try {
        sessionManager.setSessionId(null, "deepInsightsSessionId");
        const response = await dispatch(fetchDeepInsightsAlerts({ alert_id: alertId, session_id: ensureSessionId() }));

        if (response?.error) {
          console.error("Error fetching Deep Insights alert:", response.error);
          return;
        }

        setAlertData(response?.alert || response || null);
        if (response?.session_id) {
          sessionManager.setSessionId(response.session_id, "deepInsightsSessionId");
          fetchUserConversation(username, response.session_id);
        }
      } catch (error) {
        console.error("Error fetching Deep Insights alert:", error);
      } finally {
        setIsAlertLoading(false);
      }
    };
    fetchAlertSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, alertId]);

  const handleInputChange = (text) => {
    setMessage(text);
  };

  const handlePromptClick = (prompt) => {
    setMessage(prompt);
  };

  const ensureSessionId = () => {
    const existingSessionId = sessionManager.getSessionId("deepInsightsSessionId");
    if (existingSessionId && existingSessionId !== "undefined") return existingSessionId;
    try {
      const newSessionId = generateSessionId();
      sessionManager.setSessionId(newSessionId, "deepInsightsSessionId");
      return newSessionId;
    } catch (error) {
      console.error("Error generating Deep Insights session ID:", error);
      return "";
    }
  };

  const handleAbortMessageError = (
    botMessageId,
    errorMessage = "Sorry, I'm having trouble right now."
  ) => {
    setUIConversation((prev) => {
      const newConvo = [...prev];
      const lastIndex = newConvo.length - 1;
      if (newConvo[lastIndex]?.id === botMessageId || newConvo[lastIndex]?.messageId === botMessageId) {
        newConvo[lastIndex] = {
          ...newConvo[lastIndex],
          isPlaceholder: false,
          content: errorMessage,
          steps: [],
          follow_up_questions: [],
        };
      }
      return newConvo;
    });
    streamReaderRef.current = null;
    setIsLoading(false);
  };

  const updateAssistantMessage = (botMessageId, updates) => {
    setUIConversation((prev) => {
      const newConvo = [...prev];
      const lastIndex = newConvo.length - 1;
      if (newConvo[lastIndex]?.id === botMessageId || newConvo[lastIndex]?.messageId === botMessageId) {
        newConvo[lastIndex] = { ...newConvo[lastIndex], ...updates };
      }
      return newConvo;
    });
  };

  const handleRegenerate = async (index, option) => {
    const previousUserMessage = uiConversation[index - 1];
    const currentAssistantMessage = uiConversation[index];
    if (!previousUserMessage || previousUserMessage.role !== "user") return;
    if (!currentAssistantMessage || currentAssistantMessage.role !== "assistant") return;
    const messageId = currentAssistantMessage.messageId || currentAssistantMessage.id;

    await handleSendMessage({
      isRegenerate: true,
      messageID: messageId,
      regenerateIndex: index,
    });
  };

  const handleSendMessage = async (...args) => {
    let options = args[0] || {};
    let customContent = null;
    let retainVoiceOutput = false;

    if (typeof options === "string") {
      customContent = options;
      retainVoiceOutput = args[3] === true;
      options = {};
    } else {
      retainVoiceOutput = options?.retainVoiceOutput === true;
    }

    const isEdit = options?.isEdit === true;
    const isRegenerate = options?.isRegenerate === true;
    const editedConvo = options?.editedConvo || [];
    const editedMessage = options?.editedMessage || null;
    const messageID = options?.messageID || null;
    const regenerateIndex = options?.regenerateIndex;

    const content = isEdit ? editedMessage.content : (customContent || message?.trim());
    if (!isRegenerate && (!content || isLoading)) return;
    if (isRegenerate && isLoading) return;

    const wasVoiceInput = retainVoiceOutput;

    // If voice-triggered, keep mic open during streaming
    if (wasVoiceInput) {
      setKeepListeningWhileLoading(true);
      setTimeout(() => {
        chatPromptContainerRef.current?.startListening?.();
      }, 300);
    }

    if (wasVoiceInput) {
      setAutoPlayLastMessage(true);
      autoPlayLastMessageRef.current = true;
    } else {
      setAutoPlayLastMessage(false);
      autoPlayLastMessageRef.current = false;
    }

    const userMessageId = isEdit ? messageID : generateCustomId();
    const botMessageId = isRegenerate ? messageID : generateCustomId();
    const timestamp = new Date().toISOString();

    const userMessage = isEdit ? {
      ...editedMessage,
      id: userMessageId,
      messageId: userMessageId,
      timestamp,
    } : {
      role: "user",
      content,
      id: userMessageId,
      messageId: userMessageId,
      timestamp,
    };

    const placeholderBotMessage = {
      role: "assistant",
      content: "Loading...",
      isPlaceholder: true,
      id: botMessageId,
      messageId: botMessageId,
      feedback: "",
      timestamp,
      steps: [],
      autoPlayVoice: wasVoiceInput,
    };

    setUIConversation((prev) => {
      if (isEdit) {
        return [...editedConvo, userMessage, placeholderBotMessage];
      }
      if (isRegenerate) {
        return [...prev.slice(0, regenerateIndex), placeholderBotMessage];
      }
      return [...prev, userMessage, placeholderBotMessage];
    });

    if (!isEdit && !isRegenerate) {
      setMessage("");
      richInputRef.current?.clear?.();
    }
    setIsLoading(true);

    const currentSessionId = ensureSessionId();
    let currentMessageId = botMessageId;

    try {
      const abortController = new AbortController();
      streamAbortControllerRef.current = abortController;
      let response;
      if (isRegenerate) {
        const payload = {
          session_id: currentSessionId,
          message_id: botMessageId,
          ...(localStorage.getItem("playgroundModelId")
            ? { model_id: localStorage.getItem("playgroundModelId") }
            : {}),
        };
        response = await regenerateDeepInsightsChatCompletions(payload, abortController);
      } else {
        const payload = {
          user_id: username,
          ugdn_id: userUGDN || "",
          session_id: currentSessionId,
          query: content,
          stream: true,
          ...(selectedDeepInsightsSourceIds.length > 0
            ? { connector_scope: selectedDeepInsightsSourceIds }
            : {}),
          // /dev/playground only — ignored server-side for every other user.
          ...(localStorage.getItem("playgroundModelId")
            ? { model_id: localStorage.getItem("playgroundModelId") }
            : {}),
        };
        response = await fetchDeepInsightsChatCompletions(payload, abortController);
      }

      if (response?.status === 401) {
        handleUnauthorized(navigate, undefined, undefined, SESSION_EXPIRED);
        return;
      }

      if (!response.ok || !response.body) throw new Error("Streaming failed");

      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      const decoder = new TextDecoder("utf-8");

      let streamedText = "";
      let localSteps = [];
      let finalResponse = null;
      let finalResponseText = "";
      let streamedFollowUpQuestions = [];
      let hasReceivedContent = false;
      let hasParsedAnyEvent = false;
      let isStreamComplete = false;
      let leftover = "";
      let rawBody = "";

      const handleSseEvent = (event) => {
        const eventType = event?.type;
        const eventData = event?.data || event || {};

        if (eventType === SSE_EVENTS.START || eventType === SSE_EVENTS.REGENERATE_START) {
          const newId = eventData?.new_message_id || eventData?.message_id;
          if (newId) {
            currentMessageId = newId;
            updateAssistantMessage(botMessageId, {
              messageId: currentMessageId,
              id: currentMessageId
            });
          }
        } else if (eventType === SSE_EVENTS.PROGRESS) {
          const progressMsg = eventData?.message || eventData?.text || eventData?.content;
          if (!hasReceivedContent && progressMsg) {
            const newStep = progressMsg;
            if (!localSteps.includes(newStep)) {
              localSteps = [...localSteps, newStep];
            }
            setUIConversation((prev) => {
              const newConvo = [...prev];
              const lastIndex = newConvo.length - 1;
              if (newConvo[lastIndex]?.id === currentMessageId || newConvo[lastIndex]?.id === botMessageId || newConvo[lastIndex]?.messageId === botMessageId || newConvo[lastIndex]?.messageId === currentMessageId) {
                const currentSteps = newConvo[lastIndex].steps || [];
                if (!currentSteps.includes(newStep)) {
                  newConvo[lastIndex] = {
                    ...newConvo[lastIndex],
                    isPlaceholder: true,
                    steps: [...currentSteps, newStep],
                  };
                }
              }
              return newConvo;
            });
            // Speak the step aloud in parallel when this was voice-triggered
            if (autoPlayLastMessageRef.current) {
              textToSpeech.speak(newStep, { interrupt: false, rate: 1.0 });
            }
          }
        } else if (eventType === SSE_EVENTS.TOKEN) {
          const tokenText = eventData?.token || eventData?.text || eventData?.content || "";
          if (tokenText) {
            // Progress updates use browser TTS, while the answer uses the
            // streaming voice player. Stop the former before the latter starts.
            if (!hasReceivedContent && autoPlayLastMessageRef.current) {
              textToSpeech.stop();
            }
            streamedText += tokenText;
            hasReceivedContent = true;
            updateAssistantMessage(currentMessageId, {
              content: streamedText,
            });
          }
        } else if (eventType === SSE_EVENTS.FOLLOW_UP) {
          const questions = eventData?.questions || eventData?.follow_up_questions;
          if (Array.isArray(questions)) {
            streamedFollowUpQuestions = questions;
            updateAssistantMessage(currentMessageId, {
              follow_up_questions: questions,
            });
          }
        } else if (eventType === SSE_EVENTS.THREAD_TITLE) {
          // The title is persisted by the backend; refresh the sidebar as soon as it arrives.
          emit("updateThreads");
        } else if (eventType === SSE_EVENTS.FINAL) {
          finalResponse = eventData;
          // Capture the final text but defer rendering — we'll typewriter-stream it below
          // for a live-streaming feel even though the backend buffered the full response.
          finalResponseText = eventData?.response || eventData?.text || eventData?.content || "";
        } else if (eventType === SSE_EVENTS.VISUALIZATION) {
          if (eventData?.image?.startsWith?.("data:image/")) {
            // The backend sends a complete data URI; render it without Plotly.
            updateAssistantMessage(currentMessageId, {
              visualization: {
                image: eventData.image,
                title: eventData.title || null,
                format: eventData.format || "png",
              },
            });
          }
        } else if (eventType === SSE_EVENTS.CHART || eventType === "chart") {
          const graphJson = eventData?.graph_json;
          let parsedGraph = null;
          if (graphJson) {
            try {
              parsedGraph = typeof graphJson === "string" ? JSON.parse(graphJson) : graphJson;
            } catch (e) {
              console.error("Failed to parse graph JSON in handler:", e);
            }
          }
          if (parsedGraph) {
            updateAssistantMessage(currentMessageId, {
              graph: parsedGraph
            });
          }
        } else if (eventType === SSE_EVENTS.STREAM_COMPLETE) {
          isStreamComplete = true;
        } else if (eventType === SSE_EVENTS.ERROR) {
          const errorMessage =
            typeof eventData?.message === "string"
              ? eventData.message
              : "Sorry, I'm having trouble right now.";
          throw new Error(errorMessage);
        }
      };

      const parseSseLines = (lines) => {
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data:")) continue;

          let dataStr = trimmedLine.slice(5).trim();
          if (!dataStr || dataStr === "[DONE]") continue;

          try {
            const decrypted = encrypted.decrypt(dataStr);
            if (decrypted) {
              dataStr = decrypted;
            }
          } catch (e) {
            console.warn("Decryption failed or data is already plain JSON:", e);
          }

          let event = null;
          try {
            event = JSON.parse(dataStr);
          } catch {
            continue;
          }
          handleSseEvent(event);
          hasParsedAnyEvent = true;
        }
      };

      // Read response chunk-by-chunk and parse SSE events live as they arrive
      while (true) {
        const { value, done } = await reader.read();

        const decodedChunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        rawBody += decodedChunk;

        const combined = leftover + decodedChunk;
        const lines = combined.split("\n");
        leftover = lines.pop() || "";

        if (done && leftover) {
          lines.push(leftover);
          leftover = "";
        }

        parseSseLines(lines);

        if (done || isStreamComplete) break;
      }

      // Fallback: if the response was sent as a single encrypted JSON wrapper
      if (!hasParsedAnyEvent && rawBody.trim()) {
        try {
          const parsedBody = JSON.parse(rawBody);
          if (parsedBody?.encryptedBody) {
            const sseText = encrypted.decrypt(parsedBody.encryptedBody);
            parseSseLines(sseText.split("\n"));
          }
        } catch {
          // Response wasn't an encrypted JSON wrapper; nothing more to do
        }
      }

      // Stream the final response into the UI with a typewriter effect so the user
      // perceives a live token-by-token reveal even though the backend already
      // returned the full response in a single `final` event.
      if (finalResponseText && !hasReceivedContent) {
        await simulateTypewriterStream(finalResponseText, {
          tokensPerTick: 2,
          delayMs: 50,
          intervalRef: typewriterIntervalRef,
          isAborted: () => streamAbortControllerRef.current?.signal?.aborted,
          onUpdate: (content) => {
            updateAssistantMessage(currentMessageId, { content });
          },
        });
        streamedText = finalResponseText;
        hasReceivedContent = true;
      }

      const finalContent =
        streamedText || finalResponse?.response || "Sorry, I couldn't generate a response. Please try again.";

      let parsedGraph = null;
      const graphData = finalResponse?.graphs || finalResponse?.graph;
      if (graphData) {
        try {
          parsedGraph = typeof graphData === "string" ? JSON.parse(graphData) : graphData;
        } catch (e) {
          console.error("Failed to parse graph JSON:", e);
        }
      }

      const voiceText = buildDeepInsightsVoiceContent({
        role: "assistant",
        steps: localSteps,
        content: finalContent,
      }) || finalContent;

      const finalUpdates = {
        isPlaceholder: false,
        content: finalContent,
        steps: localSteps,
        spokenDuration: computeSpokenDuration(voiceText),
      };
      if (parsedGraph) {
        finalUpdates.graph = parsedGraph;
      }
      if (finalResponse?.sources) {
        finalUpdates.sources = finalResponse.sources;
      }
      const finalFollowUpQuestions =
        streamedFollowUpQuestions?.length > 0
          ? streamedFollowUpQuestions
          : finalResponse?.follow_up_questions;
      if (Array.isArray(finalFollowUpQuestions)) {
        finalUpdates.follow_up_questions = finalFollowUpQuestions;
      }
      // /dev/playground only — undefined/null for every other session, so
      // this never renders outside the playground (see MessageBox renderContent below).
      if (finalResponse?.model_used) {
        finalUpdates.model_used = finalResponse.model_used;
        finalUpdates.token_usage = finalResponse.token_usage;
        finalUpdates.tool_call_count = finalResponse.tool_call_count;
      }
      updateAssistantMessage(currentMessageId, finalUpdates);

      emit("updateThreads");
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Deep Insights chat error:", error);
      handleAbortMessageError(currentMessageId, error.message);
    } finally {
      streamReaderRef.current = null;
      streamAbortControllerRef.current = null;
      if (typewriterIntervalRef.current) {
        clearInterval(typewriterIntervalRef.current);
        typewriterIntervalRef.current = null;
      }
      setIsLoading(false);
    }
  };

  handleSendMessageRef.current = handleSendMessage;

  const stopStreaming = (explicitMessageId = null) => {
    const hadActiveStream =
      !!streamAbortControllerRef.current ||
      !!streamReaderRef.current ||
      !!typewriterIntervalRef.current;

    textToSpeech.stop();
    window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { forceAll: true } }));

    if (streamAbortControllerRef.current) {
      try {
        streamAbortControllerRef.current.abort();
      } catch (err) {
        console.warn("AbortController error:", err);
      }
      streamAbortControllerRef.current = null;
    }

    if (streamReaderRef.current) {
      try {
        const cancelPromise = streamReaderRef.current.cancel();
        if (cancelPromise && cancelPromise.catch) {
          cancelPromise.catch((err) => {
            if (err.name !== "AbortError") {
              console.warn("Stream reader cancel error:", err);
            }
          });
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Stream reader cancel error:", err);
        }
      }
      streamReaderRef.current = null;
    }

    if (typewriterIntervalRef.current) {
      clearInterval(typewriterIntervalRef.current);
      typewriterIntervalRef.current = null;
    }

    let targetMessageId = explicitMessageId;
    if (!targetMessageId) {
      const lastMsg = uiConversation[uiConversation.length - 1];
      if (lastMsg?.isPlaceholder) {
        targetMessageId = lastMsg.messageId;
      }
    }

    if (targetMessageId && targetMessageId !== lastStoppedMessageIdRef.current) {
      lastStoppedMessageIdRef.current = targetMessageId;
    }

    setIsLoading(false);
    setUIConversation((prev) => {
      const newConvo = [...prev];
      const lastIndex = newConvo.length - 1;
      if (newConvo[lastIndex]?.isPlaceholder) {
        newConvo[lastIndex] = {
          ...newConvo[lastIndex],
          isPlaceholder: false,
          content:
            newConvo[lastIndex].content === "Loading..." || !newConvo[lastIndex].content?.trim()
              ? "Streaming Stopped"
              : newConvo[lastIndex].content,
        };
      }
      return newConvo;
    });

    // Only refresh thread history if we actually stopped an active stream.
    // Prevents redundant /sessions fetch when just switching history threads.
    if (hadActiveStream) {
      setTimeout(() => {
        emit("updateThreads");
      }, 2000);
    }
  };

  // ── Alexa-mode voice session ─────────────────────────────────────────────
  const alexa = useAlexaMode({
    message,
    isLoading,
    handleSendMessage,
    stopStreaming,
    chatPromptContainerRef,
    setKeepListening: setKeepListeningWhileLoading,
  });

  const lastEditableMessageIndex = uiConversation?.reduce(
    (lastIndex, pair, index) => (pair?.role === "user" ? index : lastIndex),
    -1
  );

  const handleEditClick = (index, pair) => {
    if (pair?.role === "user" && index === lastEditableMessageIndex) {
      setEditingId(index);
      setEditedText(uiConversation[index]?.content || "");
    }
  };

  const handleSave = async (editIndex) => {
    setEditingId(null);
    if (!editedText.trim()) return;

    const editingIndex = uiConversation.findIndex(
      (msg, index) => index === editIndex && msg.role === "user"
    );
    if (editingIndex === -1) return;

    stopStreaming();

    const updatedUserMessage = {
      role: "user",
      content: sanitizeTextPreservingHTML(editedText),
    };

    const trimmedConvo = uiConversation.slice(0, editingIndex);

    await handleSendMessage({
      isEdit: true,
      editedConvo: trimmedConvo,
      editedMessage: updatedUserMessage,
      messageID: uiConversation[editingIndex]?.id || generateCustomId(),
    });

    setEditedText("");
  };

  const handleFeedback = async (index, type, data, commentParam = "") => {
    const pair = uiConversation[index];
    if (!pair) return;

    setUIConversation((prev) =>
      prev.map((msg, idx) =>
        idx === index ? { ...msg, feedback: type } : msg
      )
    );

    if (submittingFeedback) return;
    setSubmittingFeedback(true);

    try {
      const currentSessionId = ensureSessionId();

      const comment = sanitizeTextInput(
        commentParam !== "" ? commentParam : (feedbackInput?.[index]?.comment?.trim() || "")
      );

      const requestBody = {
        session_id: currentSessionId,
        message_id: pair?.messageId || pair?.id || "",
        feedback: type || "",
        feedback_comment: comment,
      };

      await dispatch(postDeepInsightsFeedback(requestBody)).unwrap?.();

      toast.info("Feedback submitted successfully");

      setFeedbackInput((prev) => ({
        ...prev,
        [index]: { comment: "" },
      }));
    } catch (error) {
      console.error("Deep Insights Feedback submit error:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (uiConversation.length > prevConversationLengthRef.current) {
      scrollToLatestViewPoint(chatEndRef, isMobile, true);
    }
    prevConversationLengthRef.current = uiConversation.length;
  }, [uiConversation, isMobile]);

  const stopAllVoiceAndStreaming = () => {
    // Reset the Alexa voice session first — clears alexaActiveRef, idle timers,
    // closes STT WebSocket, and kills TTS / Azure VOP via stopAll().
    alexa.onVoiceStop();
    stopStreaming();
    setKeepListeningWhileLoading(false);
    // Use both stopVoice (resets UI + sound) and stopListening (closes WS)
    chatPromptContainerRef.current?.stopVoice?.();
    chatPromptContainerRef.current?.stopListening?.();
    if (voiceStopWithQueryTimeoutRef.current) {
      clearTimeout(voiceStopWithQueryTimeoutRef.current);
      voiceStopWithQueryTimeoutRef.current = null;
    }
    textToSpeech.stop();
    window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { forceAll: true } }));
  };

  const handleOpenVoiceConversation = () => {
    // Both voice entry points share the same cleanup and overlay flow.
    stopAllVoiceAndStreaming();
    ensureSessionId();
    setIsVoiceConversationOpen(true);
  };

  const handleVoiceConversationClose = (voiceTurns = []) => {
    setIsVoiceConversationOpen(false);
    if (!Array.isArray(voiceTurns) || voiceTurns.length === 0) return;

    const turnsToAppend = voiceTurns
      .filter((t) => (t?.text || "").trim())
      .map((t) => {
        const id = generateCustomId();
        return {
          role: t.role || "assistant",
          content: t.text || "",
          id,
          messageId: id,
          timestamp: new Date().toISOString(),
          feedback: "",
        };
      });

    if (turnsToAppend.length === 0) return;

    setUIConversation((prev) => [...prev, ...turnsToAppend]);
    scrollToLatestViewPoint(chatEndRef, isMobile, true, true);
    emit("updateThreads");
  };

  const clearChatData = async () => {
    stopAllVoiceAndStreaming();
    setIsVoiceConversationOpen(false);
    setShowPlaybooks(false);
    setShowConnectors(false);
    setSelectedDeepInsightsSourceIds([]);
    setUIConversation([]);
    setIsChatLoading(false);
    setAlertData(null);
    // Ensure voice never auto-plays after clearing/switching sessions
    setAutoPlayLastMessage(false);
    autoPlayLastMessageRef.current = false;
    await emit("resetSelectedThread", true);
    sessionManager.setSessionId(null, "deepInsightsSessionId");
    setMessage("");
    if (richInputRef.current) {
      richInputRef.current.clear();
    }

    if (searchParams.get("alert_id")) {
      const next = new URLSearchParams(searchParams);
      next.delete("alert_id");
      setSearchParams(next, { replace: true });
    }
  };

  const createNewSession = async () => {
    sessionManager.setSessionId(null, "deepInsightsSessionId");
    const newSessionId = ensureSessionId();

    try {
      const response = await dispatch(createDeepInsightsSession({
        session_id: newSessionId,
        thread_title: ""
      }));

      if (response?.session_id)
        sessionManager.setSessionId(response?.session_id, "deepInsightsSessionId");

    } catch (error) {
      console.error("Error creating new session:", error);
    }
    return newSessionId;
  };

  const handleNewSessionContinue = async () => {
    stopAllVoiceAndStreaming();
    // Never bounce the standalone /dev/demo or /dev/playground pages back to
    // the canonical chat route — they render this component directly at
    // their own URL and have no SSO/RBAC route tree to return to.
    const isStandaloneDevRoute = location?.pathname?.startsWith(`${ROUTES.DEEP_INSIGHTS_CHAT}/dev/`);
    if (!isStandaloneDevRoute && location?.pathname !== ROUTES.DEEP_INSIGHTS_CHAT) {
      navigate(ROUTES.DEEP_INSIGHTS_CHAT);
    }
    stopStreaming();
    await emit("resetSelectedThread", true);
    clearChatData();
    setIsLoading(false);
    setIsChatLoading(false);
    await createNewSession();
  };

  const fetchUserConversation = async (user_id, session_id) => {
    setIsChatLoading(true);
    setUIConversation([]);
    setAlertData(null);
    // Always disable auto-play when loading history — old messages must never speak automatically
    setAutoPlayLastMessage(false);
    autoPlayLastMessageRef.current = false;
    if (!user_id || !session_id) {
      setIsChatLoading(false);
      handleNewSessionContinue();
      return;
    }
    try {
      const params = { session_id };
      const response = await dispatch(fetchDeepInsightsUserConversation(params));
      if (response?.session_id)
        sessionManager.setSessionId(response?.session_id, "deepInsightsSessionId")

      if (response?.error) {
        console.error("Error fetching Deep Insights user conversation:", response.error);
        return;
      }

      const conversionData = response;

      if (conversionData?.alert_context) {
        setAlertData(conversionData.alert_context);
      }

      let mappedMessages = mapConversationMessages(conversionData?.messages || [], { useRoleSuffixId: false });
      mappedMessages = mappedMessages.map(msg =>
        msg.role === "user" ? { ...msg, id: generateCustomId() } : msg
      );

      setUIConversation(mappedMessages);
      scrollToLatestViewPoint(chatEndRef, isMobile, true);
    } catch (error) {
      console.error("Error fetching Deep Insights user conversation:", error);
    } finally {
      setIsChatLoading(false);
    }
  };

  // When voice captures >2 words during TTS playback (background mode),
  // auto-send that text as a new query with voice output enabled.
  useEffect(() => {
    const handleVoiceStopWithQuery = (e) => {
      const query = e.detail?.query;
      if (!query?.trim()) return;
      if (voiceStopWithQueryTimeoutRef.current) {
        clearTimeout(voiceStopWithQueryTimeoutRef.current);
      }
      voiceStopWithQueryTimeoutRef.current = setTimeout(() => {
        handleSendMessageRef.current?.(query, false, false, true);
      }, 1500);
    };
    window.addEventListener("voiceStopWithQuery", handleVoiceStopWithQuery);
    return () => {
      window.removeEventListener("voiceStopWithQuery", handleVoiceStopWithQuery);
      if (voiceStopWithQueryTimeoutRef.current) {
        clearTimeout(voiceStopWithQueryTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe("newDeepInsightsChatDirect", () => {
      setShowPlaybooks(false);
      setShowConnectors(false);
      handleNewSessionContinue();
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe("clearChat", () => {
      clearChatData();
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sessionId = sessionManager.getSessionId("deepInsightsSessionId");
    const userId = username;
    if (sessionId && userId) {
      // fetchUserConversation(userId, sessionId);
      handleNewSessionContinue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMissedAlertClick = async (alert) => {
    if (!alert) return;
    const clickedAlertId = alert.alert_context?.alert_id || alert.alert_id;
    if (!clickedAlertId) return;

    setIsMissedAlertsModalOpen(false);
    clearChatData();
    stopStreaming();

    setIsAlertLoading(true);
    try {
      sessionManager.setSessionId(null, "deepInsightsSessionId");
      const response = await dispatch(fetchDeepInsightsAlerts({ alert_id: clickedAlertId, session_id: ensureSessionId() }));

      if (response?.error) {
        console.error("Error fetching Deep Insights alert:", response.error);
        return;
      }

      setAlertData(response?.alert || response || null);
      if (response?.session_id) {
        sessionManager.setSessionId(response.session_id, "deepInsightsSessionId");
        fetchUserConversation(username, response.session_id);
      }
    } catch (error) {
      console.error("Error fetching Deep Insights alert:", error);
    } finally {
      setIsAlertLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribe("missedAlertClicked", handleMissedAlertClick);
    const unsubscribeConnectors = subscribe("openDeepInsightsConnectors", () => {
      stopAllVoiceAndStreaming();
      setShowPlaybooks(false);
      setShowConnectors(true);
    });

    const unsubscribePlaybooks = subscribe("openDeepInsightsPlaybooks", () => {
      stopAllVoiceAndStreaming();
      setShowConnectors(false);
      setShowPlaybooks(true);
    });

    return () => {
      unsubscribe();
      unsubscribeConnectors();
      unsubscribePlaybooks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, username, subscribe]);

  useEffect(() => {
    const unsubscribe = subscribe("thread", async (agentType, threadSessionId) => {
      if (agentType !== "DEEP_INSIGHTS") return;
      if (!threadSessionId) return;

      const localSessionId = ensureSessionId();
      setEditingId(null);
      setShowPlaybooks(false);
      setShowConnectors(false);
      if (localSessionId !== threadSessionId) {
        clearChatData();
        sessionManager.setSessionId(threadSessionId, "deepInsightsSessionId");
        fetchUserConversation(username, threadSessionId);
        stopAllVoiceAndStreaming();
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, username]);

  const fetchAlerts = async () => {
    setIsMissedAlertsLoading(true);
    try {
      const response = await dispatch(fetchAllDeepInsightsAlerts());
      if (response && !response.error) {
        setMissedAlerts(Array.isArray(response) ? response : []);
      } else {
        setMissedAlerts([]);
      }
    } catch (error) {
      console.error("Failed to load alerts:", error);
      setMissedAlerts([]);
    } finally {
      setIsMissedAlertsLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isAtBottom = Math.ceil(scrollTop) + clientHeight >= scrollHeight - 100;
      setShowScrollButton(!isAtBottom);
    }
  };

  useEffect(() => {
    handleScroll();
  }, [uiConversation]);

  const visibleAlerts = (missedAlerts || []).slice(0, 1);

  const formatAlertDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    } catch {
      return dateString;
    }
  };

  // The pin reflects the state of the thread we're currently viewing, so ignore a
  // thread selection left over from a previously opened session.
  const selectedThreadName = useSelector((state) => state.state?.selectedThreadName);
  const activeThread =
    selectedThreadName?.session_id &&
      selectedThreadName.session_id === sessionManager.getSessionId("deepInsightsSessionId")
      ? selectedThreadName
      : null;
  const isThreadPinned = !!activeThread?.pin_flag;

  const handleThreadPinClick = () => {
    const currentSessionId = sessionManager.getSessionId("deepInsightsSessionId");
    if (!currentSessionId) return;
    emit("headerOpenPinModal", activeThread || {
      session_id: currentSessionId,
      thread_title: alertData?.headline || "",
      pin_flag: false,
    });
  };

  const handleAskConnector = (connectorId) => {
    setSelectedDeepInsightsSourceIds([connectorId]);
    setShowConnectors(false);
    setShowPlaybooks(false);
  };

  const renderPinButton = (withLabel = false) => (
    <button
      type="button"
      className={`di-pin-btn ${withLabel ? "with-label" : ""} ${isThreadPinned ? "pinned" : ""}`}
      onClick={handleThreadPinClick}
      title={isThreadPinned ? UNPIN_CHAT : PIN_CHAT}
      aria-label={isThreadPinned ? UNPIN_CHAT : PIN_CHAT}
    >
      <img src={isThreadPinned ? unpinIcon : pinIcon} alt="" />
      {withLabel && <span>{isThreadPinned ? UNPIN : PIN}</span>}
    </button>
  );

  return (
    <div className={`upl-genie-page-wrapper centered-cpc-page deep-insights-chat-page ${hasChatMessages ? "is-active" : "is-empty"} ${alertData?.headline ? "has-active-alert" : ""}`}>
      <div className="upl-genie-container">
        {showConnectors ? (
          <DeepInsightsConnectors
            connectors={deepInsightsSources}
            isLoading={isDeepInsightsSourcesLoading}
            onAsk={handleAskConnector}
          />
        ) : showPlaybooks ? (
          <DeepInsightsPlaybooks />
        ) : (
          <>
            <LoadingScreen isLoading={isAlertLoading || isChatLoading || (isMissedAlertsLoading && missedAlerts.length === 0)} />
            {!isAlertLoading && !isChatLoading && (!isMissedAlertsLoading || missedAlerts.length > 0) && (
              <>
            <div className="upl-chat-body" ref={chatContainerRef} onScroll={handleScroll}>
              <div className="upl-chat-content-column">
                {!hasChatMessages && !alertData?.headline && !alertData?.narration && missedAlerts.length > 0 && (
                  <div className="di-missed-alerts-container">
                    <div className="di-missed-alerts-list">
                      {Array.isArray(visibleAlerts) && visibleAlerts.map((alert, index) => (
                        <div
                          key={alert.alert_id || index}
                          className="di-missed-alert-pill"
                          onClick={() => handleMissedAlertClick(alert)}
                        >
                          <span className="di-missed-alert-dot"></span>
                          <span className="di-missed-alert-text">
                            <b>{NEW_ALERT}</b> {alert.headline}
                          </span>
                          <span className="di-missed-alert-view">{VIEW} &gt;</span>
                        </div>
                      ))}
                    </div>
                    {missedAlerts.length > 2 && (
                      <button
                        className="di-missed-alerts-view-all"
                        onClick={() => setIsMissedAlertsModalOpen(true)}
                      >
                        {VIEW_ALL_ALERTS ? VIEW_ALL_ALERTS.replace("{count}", missedAlerts.length) : `View all ${missedAlerts.length} alerts`}
                      </button>
                    )}
                  </div>
                )}

                {hasChatMessages && !alertData?.headline && (
                  <div className="di-chat-pin-toolbar">
                    {renderPinButton(true)}
                  </div>
                )}

                {!alertData?.headline ? (
                  <Dashboard
                    className="deep-insights-dashboard deep-insights-dashboard-override"
                    icon={deepInsightsIcon}
                    subtitle={localStorage.getItem("devDemoWelcomeMessage") || DEEP_INSIGHTS_SUBTITLE}
                    cardsData={devDemoCardsData}
                    gridColumns={2}
                    onPromptClick={handlePromptClick}
                  />
                ) : (
                  <div className="di-active-alert-container">
                    <div className="di-active-alert-card">
                      {renderPinButton()}
                      <div className="di-active-alert-date">
                        Triggered {formatAlertDate(alertData?.alert_date || alertData?.analysis_month)}
                      </div>
                      <div className="di-active-alert-headline">
                        {alertData?.headline}
                      </div>
                    </div>
                  </div>
                )}

                {hasChatMessages && (
                  <div className="upl-messages-list deep-insights-messages-list">
                    {uiConversation.map((pair, index) => {
                      const isLastMessage = index === uiConversation.length - 1;
                      const isLastEditableMessage =
                        pair.role === "user" && index === lastEditableMessageIndex;

                      return (
                        <MessageBox
                          key={pair.id || index}
                          pair={pair}
                          index={index}
                          editingId={editingId}
                          editedText={editedText}
                          setEditedText={setEditedText}
                          isMobile={isMobile}
                          fullname={fullName}
                          isTextBoxDisabled={isLoading}
                          showEditPrompt={isLastEditableMessage}
                          handleEditClick={handleEditClick}
                          handleSave={handleSave}
                          setEditingId={setEditingId}
                          botIcon={aiSparkleIcon}
                          isLoading={isLoading}
                          isLastMessage={isLastMessage}
                          useMathJax={false}
                          showFeedback={["like", "dislike", "copy", "regenerate"]}
                          onFeedback={handleFeedback}
                          feedbackState={pair.feedback}
                          onRegenerate={handleRegenerate}
                          showProgressSteps={pair.role === "assistant" && pair.steps?.length > 0}
                          showTimeStamp={pair.role === "user"}
                          enableVoiceOutput={true}
                          autoPlayVoice={isLastMessage ? autoPlayLastMessage : false}
                          isStreamingVop={true}
                          streamingVopWsUrl={getSpeechWsUrl("tts", "deepInsights")}
                          theme="deep-insights"
                          expandedVOP={true}
                          onVoiceOutputStart={
                            isLastMessage && autoPlayLastMessage
                              ? alexa.onVoiceOutputStart
                              : undefined
                          }
                          onVoiceOutputEnd={
                            isLastMessage && autoPlayLastMessage
                              ? alexa.onVoiceOutputEnd
                              : undefined
                          }
                          onSuggestionClick={handlePromptClick}
                          renderContent={
                            pair.role === "assistant"
                              ? () => {
                                const displayContent =
                                  pair.content === "Loading..." ? "" : pair.content || "";
                                if (!displayContent) return null;
                                return (
                                  <>
                                    <MarkdownContent
                                      content={displayContent}
                                      isStreaming={isLoading && isLastMessage}
                                      useMathJax={false}
                                      enableSectionCopy={true}
                                    />
                                    {pair.model_used && (
                                      <div className="playground-metrics">
                                        model: {pair.model_used}
                                        {pair.token_usage && (
                                          <> · tokens: {pair.token_usage.total_tokens}
                                            {" "}({pair.token_usage.prompt_tokens} in / {pair.token_usage.completion_tokens} out)</>
                                        )}
                                        {typeof pair.tool_call_count === "number" && (
                                          <> · tool calls: {pair.tool_call_count}</>
                                        )}
                                      </div>
                                    )}
                                  </>
                                );
                              }
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>

            <div className="upl-genie-footer">
              <div className="upl-chat-content-column">
                {hasChatMessages && showScrollButton && (
                  <div className="upl-scroll-btn-row">
                    <button
                      className="upl-scroll-bottom-btn"
                      onClick={() => scrollToLatestViewPoint(chatEndRef, isMobile, true, true)}
                    >
                      <img src={downArrow} alt="Scroll to bottom" />
                    </button>
                  </div>
                )}

                <ChatPromptContainer
                  ref={chatPromptContainerRef}
                  value={message}
                  onChange={handleInputChange}
                  isLoading={isLoading}
                  placeholder={DEEP_INSIGHTS_ASK_PLACEHOLDER}
                  handleSendMessage={handleSendMessage}
                  handleStopStreaming={stopStreaming}
                  richInputRef={richInputRef}
                  sendLabel={ASK}
                  theme="deep-insights"
                  onDeepInsightsPromptClick={handlePromptClick}
                  footerText={DEEP_INSIGHTS_FOOTER_TEXT}
                  onOpenVoiceConversation={handleOpenVoiceConversation}
                  deepInsightsSourcePicker={{
                    sources: deepInsightsSources,
                    isLoading: isDeepInsightsSourcesLoading,
                    selectedSourceIds: selectedDeepInsightsSourceIds,
                    onChange: setSelectedDeepInsightsSourceIds,
                  }}
                  showVoiceInput={true}
                  isVoiceStopFeature={true}
                  onVoiceStart={() => {
                    alexa.onVoiceStart();
                  }}
                  onVoiceKeyword={alexa.onVoiceKeyword}
                  onVoiceStop={() => {
                    alexa.onVoiceStop();
                  }}
                  keepListeningWhileLoading={keepListeningWhileLoading}
                  voiceWsUrl={getSpeechWsUrl("transcribe", "deepInsights")}
                />

                {!hasChatMessages && !alertData?.headline && (
                  <div className="deep-insights-empty-footer">
                    <button
                      type="button"
                      className="deep-insights-talk-btn"
                      onClick={handleOpenVoiceConversation}
                      aria-label="Talk to DeepInsights"
                    >
                      <img src={voiceConversationIcon} alt="" aria-hidden="true" />
                      <span>Talk to DeepInsights</span>
                    </button>
                    <div className="deep-insights-pills">
                      {PROMPT_CATEGORIES.map((category) => (
                        <button
                          key={category.title}
                          type="button"
                          className="deep-insights-pill-btn"
                          onClick={() => {
                            const promptToUse = (category && category.prompts && category.prompts.length > 0)
                              ? category.prompts[0]
                              : category.title;
                            handlePromptClick(promptToUse);
                          }}
                        >
                          {category.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}
          </>
        )}
      </div>

      <MissedAlertsModal
        open={isMissedAlertsModalOpen}
        onClose={() => setIsMissedAlertsModalOpen(false)}
        alerts={missedAlerts}
        isLoading={isMissedAlertsLoading}
      />

      <VoiceConversationOverlay
        isOpen={isVoiceConversationOpen}
        onClose={handleVoiceConversationClose}
        sessionId={ensureSessionId()}
      />
    </div>
  );
};

export default DeepInsightsChat;